import React, { useEffect, useMemo, useRef } from 'react';
import { ActionIcon, Box, Group, Text, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useLeadRowQuoteRepository } from '@ayocore/erp/crm/client/rpc';
import type {
  CreateLeadRowQuote,
  LeadRowQuote,
} from '@ayocore/erp/crm/schemas/lead-row-quote.schema';
import {
  quoteNotesColumn,
  quotePriceColumn,
  quoteProductNameColumn,
  quoteQtyAvailableColumn,
  quoteQtyColumn,
  createQuoteSupplierColumn,
  type SupplierQuoteColumnKey,
  type SupplierQuotePatch,
} from '@ayocore/erp/crm/client/components/Quotes/EditableGrid/Columns';
import { EditableGrid } from '@ayocore/platform/client/components/EditableGrid';
import type { EditableGridColumn } from '@ayocore/platform/client/components/EditableGrid';

type LeadRowQuotesBlockProps = {
  leadRowId: number;
};

export const LeadRowQuotesBlock = React.memo(function LeadRowQuotesBlock({
  leadRowId,
}: LeadRowQuotesBlockProps) {
  const quoteRepo = useLeadRowQuoteRepository();
  const quoteRepoRef = useRef(quoteRepo);

  useEffect(() => {
    quoteRepoRef.current = quoteRepo;
  }, [quoteRepo]);

  const columns = useMemo<
    EditableGridColumn<LeadRowQuote, SupplierQuotePatch, SupplierQuoteColumnKey>[]
  >(
    () => [
      createQuoteSupplierColumn<LeadRowQuote>(),
      quoteProductNameColumn,
      quotePriceColumn,
      quoteQtyColumn,
      quoteQtyAvailableColumn,
      quoteNotesColumn,
    ],
    []
  );

  const dataSource = useMemo(
    () => ({
      loadRows: async () => {
        const list = await quoteRepoRef.current.filterByLeadRow(leadRowId).fetch();
        return list ?? [];
      },
      createRow: async (payload: CreateLeadRowQuote) => {
        return (await quoteRepoRef.current.create(payload)) as LeadRowQuote;
      },
      updateRow: async (rowId: number, patch: SupplierQuotePatch) => {
        await quoteRepoRef.current.update(rowId, patch);
      },
      deleteRow: async (rowId: number) => {
        await quoteRepoRef.current.deleteById(rowId);
      },
      defaults: {
        lead_row_id: leadRowId,
        supplier_id: null,
        supplier: null,
        supplier_name: null,
        product_name: null,
        price: 0,
        qty: 1,
        qty_available: null,
        notes: null,
      },
    }),
    [leadRowId]
  );

  return (
    <Box py='xs' px='md'>
      <Group justify='space-between' mb='xs'>
        <Text size='xs' fw={600}>
          Подбор поставщиков
        </Text>
      </Group>

      <EditableGrid
        columns={columns}
        getRowId={row => row.id}
        dataSource={dataSource}
        loadingText='Загрузка проценок...'
        emptyText='Нет проценок. Добавьте предложение от поставщика.'
        tableMinWidth={980}
        actionColumnWidth={78}
        appendEmptyRow
        renderRowActions={({ rowId, state, actions }) => (
          <Tooltip label='Удалить'>
            <ActionIcon
              size='xs'
              variant='subtle'
              color='red'
              disabled={state.rowSaving || state.creatingRow}
              onClick={() => {
                if (!confirm('Удалить проценку?')) return;
                void actions.deleteRow(rowId);
              }}
            >
              <IconTrash size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      />
    </Box>
  );
});

LeadRowQuotesBlock.displayName = 'LeadRowQuotesBlock';
